import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";

import { useGrows } from "@/lib/api/grows";
import { useRecipes } from "@/lib/api/recipes";
import { useStartRefillSession } from "@/lib/api/refill-sessions";
import { useWaterSources } from "@/lib/api/water-sources";
import { newULID } from "@/lib/api/client";
import { useLiveStream } from "@/lib/livestream/use-livestream";
import { usePreferences } from "@/lib/preferences";
import {
  tempFromCelsius,
  tempToCelsius,
  tempUnitLabel,
} from "@/lib/units";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormField, parseOptNum } from "@/components/forms/field";
import { ErrorState } from "@/components/error-state";

// Start a refill session, then hand off to the guided 8-step wizard. This
// form is the "open the session" step: pick the grow, optional recipe pin,
// and an optional first pen reading. On success it routes to the wizard.

const TRIGGERS = ["manual", "level", "ph", "ec", "scheduled"] as const;

const optionalNumString = z
  .string()
  .optional()
  .refine(
    (v) => v === undefined || v === "" || !Number.isNaN(Number(v)),
    "must be a number",
  );

const schema = z.object({
  grow_id: z.string().min(1, "grow is required"),
  trigger: z.enum(TRIGGERS),
  decision_type: z.string().optional(),
  recipe_id: z.string().optional(),
  pre_ph: optionalNumString,
  pre_ec: optionalNumString,
  pre_temp_c: optionalNumString,
  pre_level_gal: optionalNumString,
  notes: z.string().optional(),
});
type Values = z.infer<typeof schema>;

// Radix Select reserves the empty string for "no value / show placeholder",
// so the "No pin" option uses a sentinel that gets translated back to
// undefined before the request is built.
const NO_PIN = "__none__";
// Same sentinel trick for the optional per-refill water-source override; the
// default ("Use grow default") leaves water_source_id off the request.
const USE_GROW_DEFAULT = "__grow_default__";

export function RefillNewPage() {
  const navigate = useNavigate();
  const { grow: growParam } = useSearch({ from: "/refills/new" });
  const grows = useGrows();
  const recipes = useRecipes();
  const waterSources = useWaterSources();
  const start = useStartRefillSession();
  const [activeRecipe, setActiveRecipe] = useState<string>(NO_PIN);
  const [activeWaterSource, setActiveWaterSource] =
    useState<string>(USE_GROW_DEFAULT);
  const live = useLiveStream();
  const { temperatureUnit } = usePreferences();

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { grow_id: "", trigger: "manual", notes: "" },
  });
  const err = form.formState.errors;

  function submit(values: Values) {
    const hasPre =
      values.pre_ph ||
      values.pre_ec ||
      values.pre_temp_c ||
      values.pre_level_gal;
    const pinFromSelect = activeRecipe === NO_PIN ? undefined : activeRecipe;
    const recipeID = values.recipe_id || pinFromSelect || undefined;
    // Unset ⇒ omit so the server uses the grow's default water source.
    const waterSourceID =
      activeWaterSource === USE_GROW_DEFAULT ? undefined : activeWaterSource;
    const pinned = recipeID
      ? recipes.data?.items.find((r) => r.id === recipeID)
      : undefined;
    // Form holds temp in the display unit; normalize to Celsius at the wire.
    const rawTemp = parseOptNum(values.pre_temp_c);
    const tempC =
      rawTemp === undefined ? undefined : tempToCelsius(rawTemp, temperatureUnit);
    start.mutate(
      {
        id: newULID(),
        grow_id: values.grow_id,
        trigger: values.trigger,
        decision_type: values.decision_type || undefined,
        water_source_id: waterSourceID,
        recipe_id: recipeID,
        recipe_version: pinned?.version,
        pre_state: hasPre
          ? {
              ph: parseOptNum(values.pre_ph),
              ec: parseOptNum(values.pre_ec),
              temp_c: tempC,
              level_gal: parseOptNum(values.pre_level_gal),
              captured_at: new Date().toISOString(),
            }
          : undefined,
        notes: values.notes || undefined,
      },
      {
        onSuccess: (created) =>
          navigate({
            to: "/refills/$sessionId/wizard",
            params: { sessionId: created.id },
            search: { step: 1 },
          }),
      },
    );
  }

  // Auto-pick the first active grow once the list lands. Done in an effect
  // (not during render) so the value is actually committed to form state and
  // clears validation — selecting it during render only updated the displayed
  // value, leaving `grow_id` empty at submit time.
  const growItems = grows.data?.items ?? [];
  useEffect(() => {
    if (form.getValues("grow_id")) return;
    // Prefer the grow passed via ?grow= (e.g. from a pending action) when it's
    // a real, non-archived grow; otherwise fall back to the first active one.
    // Use a ternary (not `growParam && …`) so an empty `?grow=` — a string, so
    // not caught by `??` — still falls through to the fallback.
    const preselected = growParam
      ? growItems.find((g) => g.id === growParam && !g.archived_at)
      : undefined;
    const target = preselected ?? growItems.find((g) => !g.archived_at);
    if (target)
      form.setValue("grow_id", target.id, { shouldValidate: true });
  }, [growItems, form, growParam]);

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">Start refill</h1>
      <form
        onSubmit={form.handleSubmit(submit)}
        className="space-y-6 max-w-2xl"
        noValidate
      >
        {start.error && (
          <ErrorState error={start.error} retry={() => start.reset()} />
        )}

        <FormField label="Grow" error={err.grow_id?.message}>
          <Select
            value={form.watch("grow_id")}
            onValueChange={(v) =>
              form.setValue("grow_id", v, { shouldValidate: true })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Pick a grow…" />
            </SelectTrigger>
            <SelectContent>
              {growItems
                .filter((g) => !g.archived_at)
                .map((g) => (
                  <SelectItem key={g.id} value={g.id}>
                    {g.species} — {g.system_type}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </FormField>

        <div className="grid grid-cols-2 gap-4">
          <FormField label="Trigger" error={err.trigger?.message}>
            <Select
              value={form.watch("trigger")}
              onValueChange={(v) =>
                form.setValue("trigger", v as Values["trigger"], {
                  shouldValidate: true,
                })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TRIGGERS.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Decision type" help="topoff, refresh, …">
            <Input {...form.register("decision_type")} />
          </FormField>
        </div>

        <FormField
          label="Recipe pin"
          help="Optional. Locks this session to a specific (recipe, version)."
        >
          <Select value={activeRecipe} onValueChange={setActiveRecipe}>
            <SelectTrigger>
              <SelectValue placeholder="No pin" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_PIN}>No pin</SelectItem>
              {recipes.data?.items.map((r) => (
                <SelectItem key={r.id} value={r.id}>
                  {r.name} v{r.version}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>

        <FormField
          label="Water source (override)"
          help="Defaults to the grow's water source."
        >
          <Select
            value={activeWaterSource}
            onValueChange={setActiveWaterSource}
          >
            <SelectTrigger>
              <SelectValue placeholder="Use grow default" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={USE_GROW_DEFAULT}>
                Use grow default
              </SelectItem>
              {waterSources.data?.items.map((w) => (
                <SelectItem key={w.id} value={w.id}>
                  {w.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>

        <fieldset className="space-y-2">
          <div className="flex items-center justify-between">
            <legend className="text-sm font-medium">
              Pre-state (optional)
            </legend>
            <CaptureFromPen
              onCapture={(s) => {
                const ph = s.ph_filtered ?? s.ph;
                if (typeof ph === "number")
                  form.setValue("pre_ph", ph.toFixed(2), { shouldValidate: true });
                if (typeof s.ec_ms_per_cm === "number")
                  form.setValue("pre_ec", s.ec_ms_per_cm.toFixed(2), { shouldValidate: true });
                if (typeof s.temperature_c === "number")
                  // Pen reports C; form holds display-unit strings.
                  form.setValue(
                    "pre_temp_c",
                    tempFromCelsius(s.temperature_c, temperatureUnit).toFixed(1),
                    { shouldValidate: true },
                  );
              }}
              sample={live.lastSample}
            />
          </div>
          <div className="grid grid-cols-4 gap-3">
            <FormField label="pH" error={err.pre_ph?.message}>
              <Input
                type="number"
                step="any"
                {...form.register("pre_ph")}
              />
            </FormField>
            <FormField label="EC" error={err.pre_ec?.message}>
              <Input
                type="number"
                step="any"
                {...form.register("pre_ec")}
              />
            </FormField>
            <FormField
              label={`temp ${tempUnitLabel(temperatureUnit)}`}
              error={err.pre_temp_c?.message}
            >
              <Input
                type="number"
                step="any"
                {...form.register("pre_temp_c")}
              />
            </FormField>
            <FormField label="level gal" error={err.pre_level_gal?.message}>
              <Input
                type="number"
                step="any"
                {...form.register("pre_level_gal")}
              />
            </FormField>
          </div>
        </fieldset>

        <FormField label="Notes">
          <Textarea rows={3} {...form.register("notes")} />
        </FormField>

        <Button type="submit" disabled={start.isPending}>
          {start.isPending ? "Starting…" : "Start session"}
        </Button>
      </form>
    </div>
  );
}

// CaptureFromPen fills the pre-state fields from the most-recent live pen
// sample. Disabled when no sample is on hand; shows the sample age so the
// grower knows whether it's worth pulling in or whether they should
// dip the pen fresh first.
function CaptureFromPen({
  sample,
  onCapture,
}: {
  sample: ReturnType<typeof useLiveStream>["lastSample"];
  onCapture: (s: NonNullable<ReturnType<typeof useLiveStream>["lastSample"]>["sample"]) => void;
}) {
  const fresh = sample !== null;
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      {fresh && (
        <span>
          last sample{" "}
          {formatDistanceToNow(sample!.receivedAt, { addSuffix: true })}
        </span>
      )}
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={!fresh}
        onClick={() => fresh && onCapture(sample!.sample)}
      >
        Capture from pen
      </Button>
    </div>
  );
}
